/**
 * Phone rules per country, as configured in the authentication service (`GET /api/phone-rule-configs`)
 * and used by the Waffy ID self-registration form: the national number length, the allowed operator
 * prefixes and whether a leading zero is tolerated. The server validates again; this only gives the
 * user the same answer before submitting.
 */
export interface PhoneRule {
  /** Calling code without the plus, e.g. "966". */
  countryCode: string;
  isoCode?: string;
  countryName: string;
  countryNameAr?: string;
  countryFlag?: string;
  enabled: boolean;
  nationalNumberLength: number;
  nationalNumberLengthWithZero?: number | null;
  hasExtraZeroPrefix?: boolean;
  allowedPrefixes?: string[];
  defaultOtpMethod?: string;
  resendOtpMethods?: string[];
}

/** Used when the rules cannot be loaded, so the dialog still works (Saudi mobile numbers). */
export const FALLBACK_PHONE_RULES: PhoneRule[] = [
  {
    countryCode: "966",
    isoCode: "SA",
    countryName: "Saudi Arabia",
    countryNameAr: "المملكة العربية السعودية",
    countryFlag: "🇸🇦",
    enabled: true,
    nationalNumberLength: 9,
    nationalNumberLengthWithZero: 10,
    hasExtraZeroPrefix: true,
    allowedPrefixes: ["5"],
  },
];

/** Digits only; Arabic-Indic digits are converted, like the Waffy ID registration form. */
/**
 * Lower-case ISO country code of the rule: the one the service sends or, when it sends none (it
 * does not today), the one in its flag emoji (two regional-indicator letters), as the Waffy ID
 * registration page does.
 */
export const flagIsoCode = (rule: PhoneRule): string | undefined => {
  if (rule.isoCode) return rule.isoCode.toLowerCase();
  const points = Array.from(rule.countryFlag ?? "").map((c) => c.codePointAt(0) ?? 0);
  if (points.length !== 2 || points.some((cp) => cp < 0x1f1e6 || cp > 0x1f1ff)) {
    return undefined;
  }
  return points.map((cp) => String.fromCharCode(cp - 0x1f1e6 + 97)).join("");
};

/** Saudi Arabia when it is in the list (as on the Waffy ID registration page), else the first. */
export const defaultPhoneRule = (rules: PhoneRule[]): PhoneRule | undefined =>
  rules.find((rule) => rule.countryCode === "966") ?? rules[0];

export const digitsOnly = (value: string): string =>
  `${value ?? ""}`
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/\D/g, "");

const hasZeroPrefix = (rule: PhoneRule): boolean =>
  !!rule.hasExtraZeroPrefix && !!rule.nationalNumberLengthWithZero;

/** The most digits the field accepts (the longer form when a leading zero is allowed). */
export const maxTypedLength = (rule: PhoneRule): number =>
  hasZeroPrefix(rule)
    ? (rule.nationalNumberLengthWithZero as number)
    : rule.nationalNumberLength;

/** What was typed without the local leading zero, e.g. 0512345678 -> 512345678. */
export const significantNumber = (rule: PhoneRule, typed: string): string =>
  hasZeroPrefix(rule) && typed.startsWith("0") ? typed.slice(1) : typed;

const prefixAllows = (rule: PhoneRule, typed: string): boolean => {
  const prefixes = rule.allowedPrefixes ?? [];
  if (prefixes.length === 0) return true;
  const significant = significantNumber(rule, typed);
  return prefixes.some((p) => significant.startsWith(p));
};

/** Complete and valid for the country, as typed (with or without the local zero). */
export const isValidTypedNumber = (rule: PhoneRule, typed: string): boolean => {
  if (!typed) return false;
  const lengthOk = typed.startsWith("0")
    ? hasZeroPrefix(rule) &&
      typed.length === (rule.nationalNumberLengthWithZero as number)
    : typed.length === rule.nationalNumberLength;
  return lengthOk && prefixAllows(rule, typed);
};

/**
 * Show an error while typing only when it cannot become valid any more: the start matches no
 * allowed prefix, or the number is complete but wrong (same rule as the registration form).
 */
export const hasLiveError = (rule: PhoneRule, typed: string): boolean => {
  if (!typed) return false;
  const significant = significantNumber(rule, typed);
  const prefixes = rule.allowedPrefixes ?? [];
  const prefixDead =
    prefixes.length > 0 &&
    !!significant &&
    !prefixes.some((p) => p.startsWith(significant) || significant.startsWith(p));
  const expected =
    hasZeroPrefix(rule) && typed.startsWith("0")
      ? (rule.nationalNumberLengthWithZero as number)
      : rule.nationalNumberLength;
  return prefixDead || (typed.length >= expected && !isValidTypedNumber(rule, typed));
};

/** The rule whose calling code the number starts with (the longest wins). */
export const findRuleForNumber = (
  rules: PhoneRule[],
  number: string
): PhoneRule | undefined => {
  const digits = digitsOnly(number);
  return [...rules]
    .sort((a, b) => b.countryCode.length - a.countryCode.length)
    .find((rule) => digits.startsWith(rule.countryCode));
};

/**
 * What the user typed, reduced to the national number: digits only, without the calling code (when
 * pasted with it) and without the local leading zero where the country allows it.
 */
export const toNationalNumber = (rule: PhoneRule, input: string): string => {
  let digits = digitsOnly(input);
  const longestLocal =
    rule.nationalNumberLength + (rule.hasExtraZeroPrefix ? 1 : 0);
  if (digits.startsWith(rule.countryCode) && digits.length > longestLocal) {
    digits = digits.slice(rule.countryCode.length);
  }
  if (rule.hasExtraZeroPrefix && digits.startsWith("0")) {
    digits = digits.slice(1);
  }
  return digits;
};

export const isValidNationalNumber = (
  rule: PhoneRule,
  national: string
): boolean => {
  if (national.length !== rule.nationalNumberLength) return false;
  const prefixes = rule.allowedPrefixes ?? [];
  return prefixes.length === 0 || prefixes.some((p) => national.startsWith(p));
};

/** "+<calling code><national number>", or "" when nothing was typed. */
export const toFullNumber = (rule: PhoneRule, typed: string): string => {
  const significant = significantNumber(rule, typed);
  return significant ? `+${rule.countryCode}${significant}` : "";
};

/** True when the full number (with its calling code) satisfies its country's rule. */
export const isValidFullNumber = (rules: PhoneRule[], full: string): boolean => {
  const rule = findRuleForNumber(rules, full);
  if (!rule) return false;
  const national = digitsOnly(full).slice(rule.countryCode.length);
  return isValidNationalNumber(rule, national);
};
